import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export interface EditorialHeroAction {
  label: string;
  onClick?: () => void;
  href?: string;
  variant?: 'primary' | 'secondary' | 'ghost';
  icon?: React.ReactNode;
}

export interface EditorialHeroProps {
  title: string | React.ReactNode;
  description: string | React.ReactNode;
  eyebrow?: string;
  telemetryBadge?: string;
  telemetryColor?: 'teal' | 'green' | 'amber' | 'nova';
  actions?: EditorialHeroAction[];
  showBackButton?: boolean;
  backButtonLabel?: string;
  onBackClick?: () => void;
  isAr?: boolean;
  className?: string;
  children?: React.ReactNode;
}

export const EditorialHero: React.FC<EditorialHeroProps> = ({
  title,
  description,
  eyebrow,
  telemetryBadge,
  telemetryColor = 'teal',
  actions,
  showBackButton = false,
  backButtonLabel,
  onBackClick,
  isAr = false,
  className = '',
  children,
}) => {
  const navigate = useNavigate();

  const handleBack = () => {
    if (onBackClick) {
      onBackClick();
    } else {
      navigate(-1);
    }
  };

  const colorMap = {
    teal: { text: 'text-[#0E93A3]', dot: 'bg-[#0E93A3]' },
    green: { text: 'text-[#639922]', dot: 'bg-[#639922]' },
    amber: { text: 'text-[#BA7517]', dot: 'bg-[#BA7517]' },
    nova: { text: 'text-[#7F77DD]', dot: 'bg-[#7F77DD]' },
  };

  const selectedColor = colorMap[telemetryColor];

  return (
    <div className={`w-full pb-16 border-b border-[#262C31] ${className}`}>
      {/* Optional Back Button */}
      {showBackButton && (
        <div className="mb-8">
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex items-center gap-2 text-[#9AA5AC] hover:text-[#EDF0F1] transition-colors text-xs font-mono tracking-widest uppercase"
          >
            <ArrowLeft size={14} className={isAr ? 'rotate-180' : ''} />
            <span>{backButtonLabel || (isAr ? 'العودة للخلف' : 'Back')}</span>
          </button>
        </div>
      )}

      {/* Grid: 2-Column Editorial Split */}
      <div className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-start ${isAr ? 'text-right' : 'text-left'}`}>
        
        {/* Left Column (lg:col-span-7) */}
        <div className="lg:col-span-7 space-y-4">
          {eyebrow && (
            <div className="text-xs font-mono text-[#0E93A3] uppercase tracking-[0.2em] font-semibold">
              {eyebrow}
            </div>
          )}

          <h1
            className="text-3xl md:text-5xl lg:text-[54px] font-medium text-[#EDF0F1] tracking-[-0.5px] max-w-2xl leading-[1.12]"
            style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', system-ui, sans-serif" }}
          >
            {title}
          </h1>

          {children && <div className="pt-2">{children}</div>}
        </div>

        {/* Right Column (lg:col-span-5) */}
        <div className="lg:col-span-5 space-y-6">
          <p className="text-[#9AA5AC] text-base md:text-[17px] leading-[1.6] max-w-lg font-normal">
            {description}
          </p>

          {/* Telemetry Status Indicator */}
          {telemetryBadge && (
            <div className="pt-1">
              <span className={`inline-flex items-center gap-2 font-mono text-[11px] ${selectedColor.text} uppercase tracking-[0.18em]`}>
                <span className={`w-1.5 h-1.5 rounded-full ${selectedColor.dot} animate-pulse`} />
                {telemetryBadge}
              </span>
            </div>
          )}

          {/* Action Buttons */}
          {actions && actions.length > 0 && (
            <div className={`flex flex-wrap gap-3 pt-2 ${isAr ? 'justify-end' : 'justify-start'}`}>
              {actions.map((act, idx) => {
                const isPrimary = act.variant === 'primary' || !act.variant;
                const isGhost = act.variant === 'ghost';
                
                const btnClasses = isPrimary
                  ? 'bg-[#0E93A3] text-white hover:bg-[#075C68] shadow-[0_0_15px_rgba(14,147,163,0.3)]'
                  : isGhost
                  ? 'bg-transparent text-[#9AA5AC] hover:text-[#EDF0F1] hover:bg-[#12161A]'
                  : 'bg-[#12161A] text-[#EDF0F1] border border-[#262C31] hover:border-[#0E93A3]/50';

                return (
                  <button
                    key={idx}
                    type="button"
                    onClick={act.onClick || (act.href ? () => navigate(act.href!) : undefined)}
                    className={`inline-flex items-center gap-2 px-6 py-2.5 rounded-full text-xs font-mono uppercase tracking-wider font-medium transition-all duration-200 ${btnClasses}`}
                  >
                    <span>{act.label}</span>
                    {act.icon ? act.icon : (
                      <ArrowRight size={13} className={isAr ? 'rotate-180' : ''} />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default EditorialHero;
